import { assert, naturalSort, removeFromArray, UuidGenerator, Uuids } from '@standardnotes/utils';
import { TagsToFoldersMigrationApplicator } from '../../Migrations/Applicators/TagsToFolders';
import * as Models from '@standardnotes/models';
import * as Services from '@standardnotes/services';
import { ItemRelationshipDirection } from '@standardnotes/services';
import { ContentType } from '@standardnotes/domain-core';
/**
 * The item manager is backed by the payload manager. It listens for change events from the
 * global payload manager, and converts any payloads to items, then propagates those items to
 * listeners on the item manager. When the item manager makes a change to an item, it will modify
 * items using a mutator, then emit those payloads to the payload manager. The payload manager
 * will then notify its observers (which is us), we'll convert the payloads to items,
 * and then we'll propagate them to our listeners.
 */
export class ItemManager extends Services.AbstractService {
    constructor(payloadManager, internalEventBus) {
        super(internalEventBus);
        this.payloadManager = payloadManager;
        this.internalEventBus = internalEventBus;
        this.observers = [];
        this.streamDisposers = [];
        this.payloadManager = payloadManager;
        this.systemSmartViews = this.rebuildSystemSmartViews({});
        this.createCollection();
        this.unsubChangeObserver = this.payloadManager.addObserver(ContentType.TYPES.Any, this.setPayloads.bind(this));
    }
    rebuildSystemSmartViews(criteria) {
        this.systemSmartViews = Models.BuildSmartViews(criteria);
        return this.systemSmartViews;
    }
    getCollection() {
        return this.collection;
    }
    createCollection() {
        var _a;
        this.collection = new Models.ItemCollection();
        this.navigationDisplayController = new Models.ItemDisplayController(this.collection, [ContentType.TYPES.Note, ContentType.TYPES.File], {
            sortBy: 'created_at',
            sortDirection: 'dsc',
            hiddenContentTypes: [],
        });
        this.tagDisplayController = new Models.ItemDisplayController(this.collection, [ContentType.TYPES.Tag], {
            sortBy: 'title',
            sortDirection: 'asc',
        });
        this.itemsKeyDisplayController = new Models.ItemDisplayController(this.collection, [ContentType.TYPES.ItemsKey], {
            sortBy: 'created_at',
            sortDirection: 'asc',
        });
        this.componentDisplayController = new Models.ItemDisplayController(this.collection, [ContentType.TYPES.Component], {
            sortBy: 'created_at',
            sortDirection: 'asc',
        });
        this.themeDisplayController = new Models.ItemDisplayController(this.collection, [ContentType.TYPES.Theme], {
            sortBy: 'title',
            sortDirection: 'asc',
        });
        this.smartViewDisplayController = new Models.ItemDisplayController(this.collection, [ContentType.TYPES.SmartView], {
            sortBy: 'title',
            sortDirection: 'asc',
        });
        this.fileDisplayController = new Models.ItemDisplayController(this.collection, [ContentType.TYPES.File], {
            sortBy: 'title',
            sortDirection: 'asc',
        });
        this.itemCounter = new Models.ItemCounter(this.collection, (_a = this.itemCounter) === null || _a === void 0 ? void 0 : _a.observers);
    }
    get allDisplayControllers() {
        return [
            this.navigationDisplayController,
            this.tagDisplayController,
            this.itemsKeyDisplayController,
            this.componentDisplayController,
            this.themeDisplayController,
            this.smartViewDisplayController,
            this.fileDisplayController,
        ];
    }
    get invalidItems() {
        return this.collection.invalidElements();
    }
    get invalidNonVaultedItems() {
        return this.invalidItems.filter((item) => !item.key_system_identifier);
    }
    createItemFromPayload(payload) {
        return Models.CreateDecryptedItemFromPayload(payload);
    }
    createPayloadFromObject(object) {
        return new Models.DecryptedPayload(object);
    }
    setPrimaryItemDisplayOptions(options) {
        var _a, _b, _c;
        const override = {};
        const additionalFilters = [];
        if (options.views && options.views.find((view) => view.uuid === Models.SystemViewId.AllNotes)) {
            if (options.includeArchived === undefined) {
                override.includeArchived = false;
            }
            if (options.includeTrashed === undefined) {
                override.includeTrashed = false;
            }
        }
        if (options.views && options.views.find((view) => view.uuid === Models.SystemViewId.ArchivedNotes)) {
            if (options.includeTrashed === undefined) {
                override.includeTrashed = false;
            }
        }
        if (options.views && options.views.find((view) => view.uuid === Models.SystemViewId.TrashedNotes)) {
            if (!options.includeArchived) {
                override.includeArchived = true;
            }
        }
        if (options.views && options.views.find((view) => view.uuid === Models.SystemViewId.Conflicts)) {
            additionalFilters.push((item) => this.collection.conflictsOf(item.uuid).length > 0);
        }
        this.rebuildSystemSmartViews({ ...options, ...override });
        const mostRecentVersionOfTags = (_a = options.tags) === null || _a === void 0 ? void 0 : _a.map((tag) => {
            return this.collection.find(tag.uuid);
        }).filter((tag) => tag != undefined);
        const mostRecentVersionOfSearchQuery = options.searchQuery
            ? {
                ...options.searchQuery,
                tagFilters: (_b = options.searchQuery.tagFilters) === null || _b === void 0 ? void 0 : _b.map((tag) => {
                    return this.collection.find(tag.uuid);
                }).filter((tag) => tag != undefined),
            }
            : undefined;
        const mostRecentVersionOfViews = (_c = options.views) === null || _c === void 0 ? void 0 : _c.map((view) => {
            if (Models.isSystemView(view)) {
                return this.systemSmartViews.find((systemView) => systemView.uuid === view.uuid);
            }
            return this.collection.find(view.uuid);
        }).filter((view) => view != undefined);
        const updatedOptions = {
            ...options,
            ...override,
            ...{
                tags: mostRecentVersionOfTags,
                searchQuery: mostRecentVersionOfSearchQuery,
                views: mostRecentVersionOfViews,
                hiddenContentTypes: [ContentType.TYPES.Tag],
            },
        };
        if (updatedOptions.sortBy === Models.CollectionSort.Title) {
            updatedOptions.sortDirection = updatedOptions.sortDirection === 'asc' ? 'dsc' : 'asc';
        }
        this.navigationDisplayController.setDisplayOptions({
            customFilter: Models.computeUnifiedFilterForDisplayOptions(updatedOptions, this.collection, additionalFilters),
            ...updatedOptions,
        });
        this.itemCounter.setDisplayOptions(updatedOptions);
    }
    setTagsAndViewsDisplayOptions(options) {
        const updatedOptions = {
            customFilter: Models.computeUnifiedFilterForDisplayOptions(options, this.collection),
            ...options,
        };
        this.tagDisplayController.setDisplayOptions(updatedOptions);
        this.smartViewDisplayController.setDisplayOptions(updatedOptions);
    }
    setVaultDisplayOptions(options) {
        this.navigationDisplayController.setVaultDisplayOptions(options);
        this.tagDisplayController.setVaultDisplayOptions(options);
        this.smartViewDisplayController.setVaultDisplayOptions(options);
        this.fileDisplayController.setVaultDisplayOptions(options);
        this.itemCounter.setVaultDisplayOptions(options);
    }
    getInteractableItems() {
        return this.systemSmartViews.concat(this.collection.all([
            ContentType.TYPES.Note,
            ContentType.TYPES.File,
            ContentType.TYPES.Tag,
            ContentType.TYPES.SmartView,
        ]));
    }
    getDisplayableNotes() {
        assert(this.navigationDisplayController.contentTypes.length === 2);
        return this.navigationDisplayController.items().filter(Models.isNote);
    }
    getDisplayableFiles() {
        return this.fileDisplayController.items();
    }
    getDisplayableNotesAndFiles() {
        return this.navigationDisplayController.items();
    }
    getDisplayableTags() {
        return this.tagDisplayController.items();
    }
    getDisplayableItemsKeys() {
        return this.itemsKeyDisplayController.items();
    }
    getDisplayableComponents() {
        return [...this.componentDisplayController.items(), ...this.themeDisplayController.items()];
    }
    deinit() {
        this.unsubChangeObserver();
        this.streamDisposers.length = 0;
        this.unsubChangeObserver = undefined;
        this.payloadManager = undefined;
        this.collection = undefined;
        this.itemCounter = undefined;
        this.tagDisplayController = undefined;
        this.navigationDisplayController = undefined;
        this.itemsKeyDisplayController = undefined;
        this.componentDisplayController = undefined;
        this.themeDisplayController = undefined;
        this.fileDisplayController = undefined;
        this.smartViewDisplayController = undefined;
    }
    resetState() {
        this.createCollection();
    }
    findItem(uuid) {
        const itemFromCollection = this.collection.findDecrypted(uuid);
        return itemFromCollection || this.findSystemSmartView(uuid);
    }
    findAnyItem(uuid) {
        const itemFromCollection = this.collection.find(uuid);
        return itemFromCollection || this.findSystemSmartView(uuid);
    }
    findAnyItems(uuids) {
        return this.collection.findAll(uuids);
    }
    findSystemSmartView(uuid) {
        return this.systemSmartViews.find((view) => view.uuid === uuid);
    }
    findSureItem(uuid) {
        return this.findItem(uuid);
    }
    findItems(uuids) {
        return this.collection.findAllDecrypted(uuids);
    }
    findItemsIncludingBlanks(uuids) {
        return this.collection.findAllDecryptedWithBlanks(uuids);
    }
    get items() {
        return this.collection.nondeletedElements().filter(Models.isDecryptedItem);
    }
    /** Unlock .items, this function includes error decrypting items */
    allTrackedItems() {
        return this.collection.all();
    }
    hasTagsNeedingFoldersMigration() {
        return TagsToFoldersMigrationApplicator.isApplicableToCurrentData(this);
    }
    addNoteCountChangeObserver(observer) {
        return this.itemCounter.addCountChangeObserver(observer);
    }
    allCountableNotesCount() {
        return this.itemCounter.allCountableNotesCount();
    }
    allCountableFilesCount() {
        return this.itemCounter.allCountableFilesCount();
    }
    countableNotesForTag(tag) {
        if (tag instanceof Models.SmartView) {
            if (tag.uuid === Models.SystemViewId.AllNotes) {
                return this.itemCounter.allCountableNotesCount();
            }
            throw Error('countableItemsForTag is not meant to be used for smart views.');
        }
        return this.itemCounter.countableItemsForTag(tag);
    }
    getNoteCount() {
        return this.noteCount;
    }
    addObserver(contentType, callback) {
        if (!Array.isArray(contentType)) {
            contentType = [contentType];
        }
        const observer = {
            contentType,
            callback,
        };
        this.observers.push(observer);
        const thislessObservers = this.observers;
        return () => {
            removeFromArray(thislessObservers, observer);
        };
    }
    /**
     * Returns the items that reference the given item, or an empty array if no results.
     */
    itemsReferencingItem(itemToLookupUuidFor, contentType) {
        const uuids = this.collection.uuidsThatReferenceUuid(itemToLookupUuidFor.uuid);
        let referencing = this.findItems(uuids);
        if (contentType) {
            referencing = referencing.filter((ref) => {
                return (ref === null || ref === void 0 ? void 0 : ref.content_type) === contentType;
            });
        }
        return referencing;
    }
    /**
     * Returns all items that an item directly references
     */
    referencesForItem(itemToLookupUuidFor, contentType) {
        const item = this.findSureItem(itemToLookupUuidFor.uuid);
        const uuids = item.references.map((ref) => ref.uuid);
        let references = this.findItems(uuids);
        if (contentType) {
            references = references.filter((ref) => {
                return (ref === null || ref === void 0 ? void 0 : ref.content_type) === contentType;
            });
        }
        return references;
    }
    setPayloads(data) {
        const { changed, inserted, discarded, ignored, unerrored, source, sourceKey } = data;
        const createItem = (payload) => {
            return Models.CreateItemFromPayload(payload);
        };
        const affectedContentTypes = new Set();
        const changedItems = changed.map((p) => {
            affectedContentTypes.add(p.content_type);
            return createItem(p);
        });
        const insertedItems = inserted.map((p) => {
            affectedContentTypes.add(p.content_type);
            return createItem(p);
        });
        const discardedItems = discarded.map((p) => {
            affectedContentTypes.add(p.content_type);
            return new Models.DeletedItem(p);
        });
        const ignoredItems = ignored.map((p) => {
            affectedContentTypes.add(p.content_type);
            return new Models.EncryptedItem(p);
        });
        const unerroredItems = unerrored.map((p) => {
            affectedContentTypes.add(p.content_type);
            return Models.CreateDecryptedItemFromPayload(p);
        });
        const delta = {
            changed: changedItems,
            inserted: insertedItems,
            discarded: discardedItems,
            ignored: ignoredItems,
            unerrored: unerroredItems,
        };
        this.collection.onChange(delta);
        this.itemCounter.onChange(delta);
        const affectedContentTypesArray = Array.from(affectedContentTypes.values());
        for (const controller of this.allDisplayControllers) {
            if (controller.contentTypes.some((ct) => affectedContentTypesArray.includes(ct))) {
                controller.onCollectionChange(delta);
            }
        }
        this.notifyObserversByUiAdjustingDelta(delta, source, sourceKey);
    }
    notifyObserversByUiAdjustingDelta(delta, source, sourceKey) {
        const changedItems = [];
        const insertedItems = [];
        const changedDeleted = [];
        const insertedDeleted = [];
        const changedToEncrypted = [];
        for (const item of delta.changed) {
            if (Models.isDeletedItem(item)) {
                changedDeleted.push(item);
            }
            else if (Models.isDecryptedItem(item)) {
                changedItems.push(item);
            }
            else {
                changedToEncrypted.push(item);
            }
        }
        for (const item of delta.inserted) {
            if (Models.isDeletedItem(item)) {
                insertedDeleted.push(item);
            }
            else if (Models.isDecryptedItem(item)) {
                insertedItems.push(item);
            }
        }
        const itemsToRemoveFromUI = [
            ...delta.discarded,
            ...changedDeleted,
            ...insertedDeleted,
            ...changedToEncrypted,
        ];
        this.notifyObservers(changedItems, insertedItems, itemsToRemoveFromUI, delta.ignored, delta.unerrored, source, sourceKey);
    }
    notifyObservers(changed, inserted, removed, ignored, unerrored, source, sourceKey) {
        const filter = (items, types) => {
            return items.filter((item) => {
                return types.includes(ContentType.TYPES.Any) || types.includes(item.content_type);
            });
        };
        const frozenObservers = this.observers.slice();
        for (const observer of frozenObservers) {
            const filteredChanged = filter(changed, observer.contentType);
            const filteredInserted = filter(inserted, observer.contentType);
            const filteredDiscarded = filter(removed, observer.contentType);
            const filteredIgnored = filter(ignored, observer.contentType);
            const filteredUnerrored = filter(unerrored, observer.contentType);
            if (filteredChanged.length === 0 &&
                filteredInserted.length === 0 &&
                filteredDiscarded.length === 0 &&
                filteredIgnored.length === 0 &&
                filteredUnerrored.length === 0) {
                continue;
            }
            observer.callback({
                changed: filteredChanged,
                inserted: filteredInserted,
                removed: filteredDiscarded,
                ignored: filteredIgnored,
                unerrored: filteredUnerrored,
                source,
                sourceKey,
            });
        }
    }
    /**
     * Returns an array of items that need to be synced.
     */
    getDirtyItems() {
        return this.collection.dirtyElements().filter(Models.isDecryptedOrDeletedItem);
    }
    createTemplateItem(contentType, content, override) {
        const payload = new Models.DecryptedPayload({
            uuid: UuidGenerator.GenerateUuid(),
            content_type: contentType,
            content: Models.FillItemContent(content || {}),
            ...Models.PayloadTimestampDefaults(),
            ...override,
        });
        const item = Models.CreateDecryptedItemFromPayload(payload);
        return item;
    }
    /**
     * @param item item to be checked
     * @returns Whether the item is a template (unmanaged)
     */
    isTemplateItem(item) {
        return !this.findItem(item.uuid);
    }
    getItems(contentType) {
        return this.collection.allDecrypted(contentType);
    }
    getAnyItems(contentType) {
        return this.collection.all(contentType);
    }
    itemsMatchingPredicate(contentType, predicate) {
        return this.itemsMatchingPredicates(contentType, [predicate]);
    }
    itemsMatchingPredicates(contentType, predicates) {
        const subItems = this.getItems(contentType);
        return this.subItemsMatchingPredicates(subItems, predicates);
    }
    subItemsMatchingPredicates(items, predicates) {
        const results = items.filter((item) => {
            for (const predicate of predicates) {
                if (!item.satisfiesPredicate(predicate)) {
                    return false;
                }
            }
            return true;
        });
        return results;
    }
    getRootTags() {
        return this.getDisplayableTags().filter((tag) => tag.parentId === undefined);
    }
    findTagByTitle(title) {
        const lowerCaseTitle = title.toLowerCase();
        return this.getDisplayableTags().find((tag) => { var _a; return ((_a = tag.title) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === lowerCaseTitle; });
    }
    findTagByTitleAndParent(title, parentItemToLookupUuidFor) {
        const lowerCaseTitle = title.toLowerCase();
        const tags = parentItemToLookupUuidFor ? this.getTagChildren(parentItemToLookupUuidFor) : this.getRootTags();
        return tags.find((tag) => { var _a; return ((_a = tag.title) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === lowerCaseTitle; });
    }
    /**
     * Finds tags with title or component starting with a search query and (optionally) not associated with a note
     * @param searchQuery - The query string to match
     * @param note - The note whose tags should be omitted from results
     * @returns Array containing tags matching search query and not associated with note
     */
    searchTags(searchQuery, note) {
        return naturalSort(this.getDisplayableTags().filter((tag) => {
            const expandedTitle = this.getTagLongTitle(tag);
            const matchesQuery = expandedTitle.toLowerCase().includes(searchQuery.toLowerCase());
            const tagInNote = note ? this.itemsReferencingItem(note).some((item) => (item === null || item === void 0 ? void 0 : item.uuid) === tag.uuid) : false;
            return matchesQuery && !tagInNote;
        }), 'title');
    }
    getTagParent(itemToLookupUuidFor) {
        const tag = this.findItem(itemToLookupUuidFor.uuid);
        if (!tag) {
            return undefined;
        }
        const parentId = tag.parentId;
        if (parentId) {
            return this.findItem(parentId);
        }
        return undefined;
    }
    getDisplayableTagParent(itemToLookupUuidFor) {
        const tag = this.findItem(itemToLookupUuidFor.uuid);
        if (!tag) {
            return undefined;
        }
        const parentId = tag.parentId;
        if (parentId) {
            return this.tagDisplayController.items().find((displayableTag) => displayableTag.uuid === parentId);
        }
        return undefined;
    }
    getTagPrefixTitle(tag) {
        const hierarchy = this.getTagParentChain(tag);
        if (hierarchy.length === 0) {
            return undefined;
        }
        const prefixTitle = hierarchy.map((tag) => tag.title).join('/');
        return `${prefixTitle}/`;
    }
    getTagLongTitle(tag) {
        const hierarchy = this.getTagParentChain(tag);
        const tags = [...hierarchy, tag];
        const longTitle = tags.map((tag) => tag.title).join('/');
        return longTitle;
    }
    getTagParentChain(itemToLookupUuidFor) {
        const tag = this.findItem(itemToLookupUuidFor.uuid);
        if (!tag) {
            return [];
        }
        let parentId = tag.parentId;
        const chain = [];
        while (parentId) {
            const parent = this.findItem(parentId);
            if (!parent) {
                return chain;
            }
            chain.unshift(parent);
            parentId = parent.parentId;
        }
        return chain;
    }
    getTagChildren(itemToLookupUuidFor) {
        const tag = this.findItem(itemToLookupUuidFor.uuid);
        if (!tag) {
            return [];
        }
        const tags = this.collection.elementsReferencingElement(tag, ContentType.TYPES.Tag);
        return tags.filter((tag) => tag.parentId === itemToLookupUuidFor.uuid);
    }
    getDeepTagChildren(itemToLookupUuidFor) {
        const allChildren = [];
        const children = this.getTagChildren(itemToLookupUuidFor);
        for (const child of children) {
            allChildren.push(child);
            allChildren.push(...this.getDeepTagChildren(child));
        }
        return allChildren;
    }
    isTagAncestor(tagToLookUpUuidFor, childToLookUpUuidFor) {
        const tag = this.findItem(childToLookUpUuidFor.uuid);
        if (!tag) {
            return false;
        }
        let parentId = tag.parentId;
        while (parentId) {
            if (parentId === tagToLookUpUuidFor.uuid) {
                return true;
            }
            const parent = this.findItem(parentId);
            if (!parent) {
                return false;
            }
            parentId = parent.parentId;
        }
        return false;
    }
    isValidTagParent(parentTagToLookUpUuidFor, childToLookUpUuidFor) {
        if (parentTagToLookUpUuidFor.uuid === childToLookUpUuidFor.uuid) {
            return false;
        }
        if (this.isTagAncestor(childToLookUpUuidFor, parentTagToLookUpUuidFor)) {
            return false;
        }
        return true;
    }
    /**
     * Get tags for a note sorted in natural order
     * @param item - The item whose tags will be returned
     * @returns Array containing tags associated with an item
     */
    getSortedTagsForItem(item) {
        return naturalSort(this.itemsReferencingItem(item).filter((ref) => {
            return (ref === null || ref === void 0 ? void 0 : ref.content_type) === ContentType.TYPES.Tag;
        }), 'title');
    }
    getUnsortedTagsForItem(item) {
        return this.itemsReferencingItem(item).filter((ref) => {
            return (ref === null || ref === void 0 ? void 0 : ref.content_type) === ContentType.TYPES.Tag;
        });
    }
    isSmartViewTitle(title) {
        return title.startsWith(Models.SMART_TAG_DSL_PREFIX);
    }
    notesMatchingSmartView(view) {
        const criteria = {
            views: [view],
        };
        return Models.notesAndFilesMatchingOptions(criteria, this.collection.allDecrypted(ContentType.TYPES.Note), this.collection);
    }
    get allNotesSmartView() {
        return this.systemSmartViews.find((tag) => tag.uuid === Models.SystemViewId.AllNotes);
    }
    get archivedSmartView() {
        return this.systemSmartViews.find((tag) => tag.uuid === Models.SystemViewId.ArchivedNotes);
    }
    get trashSmartView() {
        return this.systemSmartViews.find((tag) => tag.uuid === Models.SystemViewId.TrashedNotes);
    }
    get untaggedNotesSmartView() {
        return this.systemSmartViews.find((tag) => tag.uuid === Models.SystemViewId.UntaggedNotes);
    }
    get trashedItems() {
        return this.notesMatchingSmartView(this.trashSmartView);
    }
    /**
     * Returns all smart views, sorted by title.
     */
    getSmartViews() {
        const userTags = this.smartViewDisplayController.items();
        return this.systemSmartViews.concat(userTags);
    }
    /**
     * The number of notes currently managed
     */
    get noteCount() {
        return this.collection.all(ContentType.TYPES.Note).length;
    }
    /**
     * Immediately removes all items from mapping state and notifies observers
     * Used primarily when signing into an account and wanting to discard any current
     * local data.
     */
    async removeAllItemsFromMemory() {
        const uuids = Uuids(this.items);
        const results = [];
        for (const uuid of uuids) {
            const mutator = new Models.DeleteItemMutator(this.findSureItem(uuid), 
            /** We don't want to set as dirty, since we want to dispose of immediately. */
            Models.MutationType.NonDirtying);
            results.push(mutator.getDeletedResult());
        }
        await this.payloadManager.emitPayloads(results, Models.PayloadEmitSource.LocalChanged);
        this.resetState();
        this.payloadManager.resetState();
    }
    /**
     * Important: Caller must coordinate with storage service separately to delete item from persistent database.
     */
    removeItemFromMemory(item) {
        this.removeItemsFromMemory([item]);
    }
    /**
     * Important: Caller must coordinate with storage service separately to delete item from persistent database.
     */
    removeItemsFromMemory(items) {
        this.collection.discard(items);
        this.payloadManager.removePayloadLocally(items.map((item) => item.payload));
        const delta = Models.CreateItemDelta({ discarded: items });
        const affectedContentTypes = items.map((item) => item.content_type);
        for (const controller of this.allDisplayControllers) {
            if (controller.contentTypes.some((ct) => affectedContentTypes.includes(ct))) {
                controller.onCollectionChange(delta);
            }
        }
    }
    relationshipDirectionBetweenItems(itemA, itemB) {
        const itemAReferencesItemB = !!itemA.references.find((reference) => reference.uuid === itemB.uuid);
        const itemBReferencesItemA = !!itemB.references.find((reference) => reference.uuid === itemA.uuid);
        return itemAReferencesItemB
            ? ItemRelationshipDirection.AReferencesB
            : itemBReferencesItemA
                ? ItemRelationshipDirection.BReferencesA
                : ItemRelationshipDirection.NoRelationship;
    }
    conflictsOf(uuid) {
        return this.collection.conflictsOf(uuid);
    }
    numberOfNotesWithConflicts() {
        const uuids = this.collection.uuidsOfItemsWithConflicts();
        const items = this.navigationDisplayController.hasExclusiveVaultOptions()
            ? this.navigationDisplayController
                .items()
                .filter((item) => Models.isNote(item) && this.collection.uuidsOfItemsWithConflicts().includes(item.uuid))
            : this.findItems(uuids).filter(Models.isNote);
        return items.length;
    }
    getItemLinkedFiles(item) {
        return item.content_type === ContentType.TYPES.File
            ? this.referencesForItem(item).filter(Models.isFile)
            : this.itemsReferencingItem(item).filter(Models.isFile);
    }
    getItemLinkedNotes(item) {
        return this.referencesForItem(item).filter(Models.isNote);
    }
    /**
     * Begin streaming items to display in the UI. The stream callback will be called
     * immediately with the present items that match the constraint, and over time whenever
     * items matching the constraint are added, changed, or deleted.
     */
    streamItems(contentType, stream) {
        const removeItemManagerObserver = this.addObserver(contentType, ({ changed, inserted, removed, source }) => {
            stream({ changed, inserted, removed, source });
        });
        const matches = this.getItems(contentType);
        stream({
            inserted: matches,
            changed: [],
            removed: [],
            source: Models.PayloadEmitSource.InitialObserverRegistrationPush,
        });
        this.streamDisposers.push(removeItemManagerObserver);
        return () => {
            removeItemManagerObserver();
            removeFromArray(this.streamDisposers, removeItemManagerObserver);
        };
    }
}
